/* eslint-disable react/jsx-no-useless-fragment */

import { AccelFile } from "@axl/membership-sdk";
import { notification, Image, Modal } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { DndContext, closestCenter, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';

import { useDrawer, useFileProvider, useLoc, useSdk } from "@app/shared/lib/hooks";
import { saveAs } from "@app/shared/lib/save-as";

import { FileGridItem } from "./FileGridItem/FileGridItem";
import { FileListItem } from "./FileListItem/FileListItem";

import styles from "./FileList.module.scss";
import { FileProvider } from "@app/shared/lib/file-provider";
import type { FileUploadArgs } from "../FileUploader";

export declare type FileIconColor = "#ffffff" | "#f1f1f1";
export declare type FileListViewType = "list" | "grid";
export declare type FileListSize = "xs" | "sm" | "md" | "lg" | "xl";

export type FilesProps = {
    files: AccelFile[];
    /**
     * Can edit filename
     */
    editable?: boolean;
    downloadable?: boolean;
    draggable?: boolean;
    uploadArgs?: FileUploadArgs;
    view?: FileListViewType;
    size?: FileListSize;
    deletable?: boolean;
    replacable?: boolean;
    dateCreatedVisible?: boolean;
    descriptionVisible?: boolean;
    beforeUpload?: (file: AccelFile) => Promise<void | boolean>;
    beforeRemove?: (file: AccelFile) => Promise<void | boolean>;
    onRemoved?: (file: AccelFile) => void;
    onAborted?: (file: AccelFile) => void;
    onUploaded?: (file: AccelFile) => void;
    onDownload?: (file: AccelFile) => void;
    onDrop?: (srcIndex: number, dstIndex: number) => void;
    removeFromServer?: boolean;
    acceptFiles?: string;
    fileIconColor?: FileIconColor;
    onFileUpdate?: (file: AccelFile) => void;
};

export type FileProps = {
    fileProvider: FileProvider;
    file: AccelFile;
    index: number;
    size: FileListSize;
    editable: boolean;
    deletable: boolean;
    downloadable: boolean;
    draggable: boolean;
    uploadArgs: FileUploadArgs;
    replaceable: boolean;
    descriptionVisible: boolean;
    dateCreatedVisible: boolean;
    fileIconColor: FileIconColor;
    beforeUpload?: (file: AccelFile) => Promise<void | boolean>;
    onUploaded?: (file: AccelFile) => void;
    onAborted?: (file: AccelFile) => void;
    onRemove?: (file: AccelFile) => void;
    onChange?: (file: AccelFile, changes: Partial<AccelFile>) => Promise<boolean>;
    onReplace?: (file: AccelFile) => void;
    onDownload?: (file: AccelFile) => void;
    onPreview: (file: AccelFile) => void;
};

export const Files: React.FC<FilesProps> = ({
    beforeRemove,
    removeFromServer = true,
    onRemoved,
    onDownload,
    editable = true,
    downloadable = true,
    deletable = true,
    uploadArgs = {},
    size = "md",
    replacable = false,
    dateCreatedVisible = false,
    descriptionVisible = false,
    draggable = false,
    fileIconColor,
    onAborted,
    onUploaded,
    beforeUpload,
    view = "list",
    files = [],
    onDrop,
    acceptFiles,
    onFileUpdate,
}) => {
    const sdk = useSdk();
    const loc = useLoc();
    const fileProvider = useFileProvider();
    const fileReplaceInput = useRef<HTMLInputElement>(null);
    const [fileToReplace, setFileToReplace] = useState<AccelFile | null>(null);

    const previewModal = useDrawer<AccelFile>();

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const remove = useCallback(
        async (file: AccelFile) => {
            if (beforeRemove != null)
                if ((await beforeRemove(file)) === false) return;
            if (removeFromServer === true) {
                const res = await sdk.api.file.remove(file.id);
                if (res.success) onRemoved?.(file);
            } else {
                onRemoved?.(file);
            }
        },
        [beforeRemove, onRemoved, removeFromServer],
    );

    const save = useCallback(
        async (file: AccelFile, changes: Partial<AccelFile>) => {
            const res = await sdk.api.file.save({ ...changes, id: file.id });
            file.update(changes);
            onFileUpdate?.(new AccelFile(file));

            return res.success;
        },
        [onFileUpdate],
    );

    const replaceFile = useCallback(
        (nativeFile: File) => {
            if (!fileToReplace) return;

            const file = AccelFile.fromFile(nativeFile);
            if (file.extension !== fileToReplace.extension) {
                setFileToReplace(null);
                notification.error({
                    message: loc.word("ErrorFileReplaceExtensionError", {
                        default:
                            "The file extensions to replace must match the extension of the original file",
                    }),
                    duration: 3,
                });

                return;
            }

            fileToReplace.replaceFile(nativeFile);
            setFileToReplace(null);
        },
        [fileToReplace],
    );

    const previewFile = useCallback(
        async (file: AccelFile) => {
            if (file.isImage || file.isVideo) {
                previewModal.open(file);
                return;
            }

            // store.preview(file);
            const url = fileProvider.getDownloadUrl(file, true);
            saveAs(url, false, file.filename);
        },
        [previewModal],
    );

    const downloadFile = useCallback(
        async (file: AccelFile) => {
            const url = fileProvider.getDownloadUrl(file, true);
            saveAs(url, false, file.filename);
            onDownload?.(file);
        },
        [onDownload],
    );

    const handleDragEnd = useCallback((event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = files.findIndex(file => file.key === active.id);
            const newIndex = files.findIndex(file => file.key === over.id);
            onDrop?.(oldIndex, newIndex);
        }
    }, [files, onDrop]);

    const fileProps = useMemo<Omit<FileProps, "file" | "index">>(
        () => ({
            fileProvider,
            editable: editable ?? true,
            downloadable: downloadable ?? true,
            uploadArgs: uploadArgs ?? {},
            size: size!,
            deletable: deletable ?? true,
            replaceable: replacable ?? false,
            dateCreatedVisible: dateCreatedVisible ?? false,
            descriptionVisible: descriptionVisible ?? false,
            draggable: draggable ?? false,
            fileIconColor: fileIconColor ?? "#ffffff",
            onRemove: (file) => remove(file),
            onAborted,
            onUploaded,
            onChange: (file, changes) => save(file, changes),
            beforeUpload,
            onDownload: (file) => downloadFile(file),
            onReplace: (file) => {
                setFileToReplace(file);

                // dirty hack (apply file ext for file input accept)
                setTimeout(() => {
                    fileReplaceInput.current?.click();
                }, 50);
            },
            onPreview: (file) => previewFile(file),
        }),
        [
            beforeUpload,
            dateCreatedVisible,
            deletable,
            descriptionVisible,
            downloadFile,
            downloadable,
            draggable,
            editable,
            fileIconColor,
            onAborted,
            onUploaded,
            previewFile,
            remove,
            replacable,
            save,
            size,
            uploadArgs,
        ],
    );

    const fileIds = files.map(file => file.key);
    const sortingStrategy = view === 'grid' ? rectSortingStrategy : verticalListSortingStrategy;
    const collisionDetection = view === 'grid' ? closestCorners : closestCenter;

    return (
        <>
            <div className={styles.fl} data-view={view}>
                {draggable
                    ? <DndContext
                        sensors={sensors}
                        collisionDetection={collisionDetection}
                        onDragEnd={handleDragEnd}>
                        <SortableContext items={fileIds} strategy={sortingStrategy}>
                            {view === 'grid'
                                ? files.map((x, i) => <FileGridItem key={x.key}
                                    file={x}
                                    index={i}
                                    {...fileProps}
                                />)
                                : files.map((x, i) => <FileListItem key={x.key}
                                    file={x}
                                    index={i}
                                    {...fileProps}
                                />)}
                        </SortableContext>
                    </DndContext>
                    : view === 'grid'
                        ? files.map((x, i) => <FileGridItem key={x.key}
                            file={x}
                            index={i}
                            {...fileProps}
                        />)
                        : files.map((x, i) => <FileListItem key={x.key}
                            file={x}
                            index={i}
                            {...fileProps}
                        />)}
            </div>

            {replacable && (
                <input
                    ref={fileReplaceInput}
                    type="file"
                    accept={fileToReplace?.extension ?? acceptFiles}
                    className="d-none"
                    onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!e.target.files || e.target.files.length === 0) return;
                        replaceFile(e.target.files[0]!);
                    }}
                />
            )}

            <Modal open={previewModal.data?.isVideo && previewModal.visible}
                onCancel={() => previewModal.close()}
                footer={null}
                centered
                width="90vw"
                style={{ maxWidth: '90vw' }}
                destroyOnHidden
                styles={{
                    body: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 0
                    }
                }}>
                {(previewModal.data?.isVideo && previewModal.visible) &&
                    <div className="relative flex flex-center w-100"
                        style={{
                            aspectRatio: '16/9',
                            maxHeight: '85vh',
                        }}>
                        {previewModal.data!.isExternalFile
                            ? <iframe src={previewModal.data!.url}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                }} />
                            : <video
                                src={fileProvider.getVideoUrl(previewModal.data!)}
                                controls
                                autoPlay={false}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 4
                                }}
                            />}
                    </div>}
            </Modal>

            {previewModal.data?.isImage && <Image
                width={0}
                height={0}
                style={{ display: 'none' }}
                preview={{
                    visible: previewModal.visible,
                    src: fileProvider.getUrl(previewModal.data!) ?? undefined,
                    onVisibleChange: () => {
                        previewModal.close();
                    },
                }}
            />}

        </>
    );
};
